"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import type { BlogPost } from "@/lib/content/blog";

type CategoryFilter = "Kõik" | "PPF" | "Keraamika" | "Toonimine" | "Hooldus";

const filters: CategoryFilter[] = ["Kõik", "PPF", "Keraamika", "Toonimine", "Hooldus"];

const categoryLookup: Record<BlogPost["category"], CategoryFilter> = {
  ppf: "PPF",
  keraamika: "Keraamika",
  toonimine: "Toonimine",
  hooldus: "Hooldus",
};

interface RelatedService {
  title: string;
  description: string;
  href: string;
  image: string;
}

interface BlogSidebarProps {
  posts: BlogPost[];
  currentSlug: string;
  defaultCategory: BlogPost["category"];
  relatedServices: RelatedService[];
}

export default function BlogSidebar({ posts, currentSlug, defaultCategory, relatedServices }: BlogSidebarProps) {
  const [activeFilter, setActiveFilter] = useState<CategoryFilter>(categoryLookup[defaultCategory] ?? "Kõik");

  const filteredPosts = useMemo(() => {
    return posts
      .filter((item) => item.slug !== currentSlug && (activeFilter === "Kõik" || categoryLookup[item.category] === activeFilter))
      .slice(0, 3);
  }, [activeFilter, posts, currentSlug]);

  return (
    <aside className="space-y-6 min-w-0 max-w-full lg:sticky lg:top-24 lg:h-fit">
      <div className="rounded-sm border border-white/10 bg-secondary p-6">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-heading text-xl font-semibold text-text-main">Sarnased artiklid</h2>
          <span className="text-xs uppercase tracking-[0.2em] text-accent">{filteredPosts.length}</span>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {filters.map((filter) => {
            const isActive = filter === activeFilter;

            return (
              <button
                key={filter}
                type="button"
                onClick={() => setActiveFilter(filter)}
                className={`rounded-sm px-3 py-1.5 text-xs font-medium transition ${
                  isActive ? "bg-accent text-primary" : "border border-white/10 bg-primary/60 text-text-main hover:border-accent/40"
                }`}
              >
                {filter}
              </button>
            );
          })}
        </div>

        <div className="mt-4 space-y-3">
          {filteredPosts.length > 0 ? (
            filteredPosts.map((item) => (
              <Link
                key={item.slug}
                href={`/blog/${item.slug}`}
                className="group flex gap-3 rounded-sm border border-white/10 bg-primary/40 p-3 transition hover:border-accent/50 hover:text-accent"
              >
                <div className="relative h-[58px] w-[58px] flex-shrink-0 overflow-hidden rounded-sm bg-secondary/50">
                  {item.coverImage && (
                    <Image
                      src={item.coverImage}
                      alt={item.title}
                      fill
                      className="object-cover grayscale-[40%] transition-[filter] duration-300 group-hover:grayscale-0"
                      sizes="58px"
                    />
                  )}
                  {!item.coverImage && (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-accent/30 to-accent/10 text-xs font-bold text-accent">
                      {item.title.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-main line-clamp-2">{item.title}</p>
                  <p className="mt-1 text-xs text-text-muted">{item.readingTime}</p>
                </div>
              </Link>
            ))
          ) : (
            <p className="text-sm text-text-muted">Selles kategoorias ei ole veel teisi artikleid.</p>
          )}
        </div>
      </div>

      <div className="rounded-sm border border-white/10 bg-secondary p-6">
        <h2 className="font-heading text-xl font-semibold text-text-main">Populaarsed teenused</h2>
        <div className="mt-4 space-y-3">
          {relatedServices.map((service) => (
            <Link
              key={service.href}
              href={service.href}
              className="group flex gap-3 rounded-sm border border-white/10 bg-primary/40 p-3 transition hover:border-accent/50"
            >
              <div className="relative h-[58px] w-[58px] flex-shrink-0 overflow-hidden rounded-sm bg-secondary/50">
                <Image
                  src={service.image}
                  alt={service.title}
                  fill
                  className="object-cover transition duration-300 group-hover:scale-105"
                  sizes="58px"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-text-main line-clamp-2">{service.title}</p>
                <p className="mt-1 text-xs leading-relaxed text-text-muted line-clamp-1">{service.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </aside>
  );
}
